export const en = {

  // ── App shell ────────────────────────────────────────────────────────
  app: {
    skipToContent: 'Skip to main content',
    header: {
      toggleNav: 'Toggle navigation',
      home: 'Home',
      textSize: 'Text size',
      chooseTheme: 'Choose theme',
    },
    themes: {
      dark: 'Dark',
      light: 'Light',
      hcDark: 'B&W Dark',
      hcLight: 'B&W Light',
      blue: 'Blue',
    },
    fontSizes: {
      md: 'Normal text size',
      lg: 'Large text size',
      xl: 'Extra large text size',
    },
    footer: {
      badgeAlt: 'Daggerheart Compatible',
      kofi: 'Support on Ko-fi',
      about: 'Info & Contact',
      privacy: 'Privacy Policy',
    },
  },

  // ── Sidebar / navigation ─────────────────────────────────────────────
  nav: {
    ariaLabel: 'Main navigation',
    closeAria: 'Close navigation',
    newBadge: 'New',
    betaBadge: 'Beta',
    logo: {
      title: 'DHTOOLS',
      subtitle: 'Daggerheart Reference',
    },
    groups: {
      gettingStarted: 'Getting Started',
      coreMaterials: 'Core Materials',
      rulesAndMechanics: 'Rules & Mechanics',
      gameMaster: 'Running an Adventure',
    },
    items: {
      home: 'Home',
      introduction: 'Introduction',
      characterCreation: 'Character Creation',
      classes: 'Classes',
      ancestries: 'Ancestries',
      communities: 'Communities',
      domains: 'Domains',
      cardLibrary: 'Card Library',
      coreMechanics: 'Core Mechanics',
      equipment: 'Equipment',
      itemsConsumables: 'Items & Consumables',
      beastforms: 'Beastforms',
      gmGuidance: 'GM Guidance',
      adversaries: 'Adversaries',
      environments: 'Environments',
      witherwildFrame: 'Witherwild Frame',
      transformations: 'Transformations',
      kohd: 'Kohd Writer',
    },
  },

  // ── Search bar ───────────────────────────────────────────────────────
  search: {
    label: 'Search DHTools',
    placeholder: 'Search...',
    resultsAria: 'Search results',
  },

  // ── About page ───────────────────────────────────────────────────────
  about: {
    title: 'About DHTools',
    projectHeading: 'The Project',
    projectText: 'DHTools is a quick-reference tool for Daggerheart. Not affiliated with Darrington Press or Critical Role.',
    makerHeading: 'About the Maker',
    makerText: "I'm Waleed, a Software Developer who graduated in 2025. Since then I've been building tools to support my hobbies, build my portfolio and hoping they help you and the TTRPG community as well.",
    accuracyHeading: 'Accuracy',
    accuracyText: 'Some values or wording may differ from the physical book or official SRD. These are editorial mistakes, not intentional changes to the rules. If you spot something, use the bug report button at the bottom of the page.',
    daggerforgeHeading: 'DaggerForge',
    daggerforgeText: 'Check out my other work for Daggerheart, an Obsidian plugin, all the necessary tools a Game Master would need!',
    daggerforgeGithub: 'GitHub',
    daggerforgePlugin: 'Obsidian Plugin',
    contactHeading: 'Contact',
    contactText: 'For questions, feedback, or collaboration:',
    contactLinkedIn: 'LinkedIn',
    licenseHeading: 'License',
    srd: 'Content from the Daggerheart SRD v1.0, © 2025 Critical Role LLC. Published under the',
    licenseLink: 'Darrington Press Community Gaming License',
    badgeAlt: 'Daggerheart Compatible',
    supportHeading: 'Support',
    supportText: 'If you find DHTools useful, you can support its development on Ko-fi.',
    kofi: 'Support on Ko-fi',
  },

  // ── Scroll to top button ─────────────────────────────────────────────
  scrollToTop: 'Scroll to top',

  // ── Shared filter / sort UI ──────────────────────────────────────────
  filters: {
    filtersLabel: 'Filters',
    resetTitle: 'Reset all filters and sorting',
    resetLabel: 'Reset',
    filtersToggle: '⚙ Filters',
    sortLabel: 'Sort',
    tierLabel: 'Tier',
    traitLabel: 'Trait',
    typeLabel: 'Type',
    roleLabel: 'Role',
    all: 'All',
    contents: 'Contents',
  },

  // ── Home ─────────────────────────────────────────────────────────────
  home: {
    heroKicker: 'Daggerheart SRD // Hope & Fear',
    heroTitle: 'DHTOOLS',
    heroSubtitle: 'A quick-reference companion for players and Game Masters running Daggerheart at the table.',
    ctaBuild: 'Build a character',
    ctaLearn: 'Learn to play',
    statsAria: 'At a glance',
    stats: {
      classes: 'Classes',
      ancestries: 'Ancestries',
      communities: 'Communities',
      cards: 'Domain cards',
      adversaries: 'Adversaries',
      environments: 'Environments',
    },
    toolsHeading: 'Tools for the table',
    tools: [
      { path: '/character-creation', title: 'Character Creation', desc: 'Build a hero in one quick page or nine detailed steps, level up, and print official-style sheets.' },
      { path: '/gm-tools?tab=villain%20builder', title: 'Villain Builder', desc: 'Reign of the Weredragon villain prompts, laid out like the printed cut-out sheet.' },
      { path: '/gm-tools?tab=doom%20track', title: 'Doom Track', desc: 'Mark the growing disasters each session; your marks stay saved on this device.' },
      { path: '/kohd', title: 'Kohd Writer', desc: 'Type a sentence and watch it become a Kohd circuit you can bend, move and share.' },
    ],
    libraryHeading: 'Explore the library',
    descs: {
      introduction: 'Rules overview & key concepts',
      characterCreation: 'The nine steps of a new hero',
      classes: 'Classes & subclasses',
      ancestries: 'Playable ancestries',
      communities: 'Community backgrounds',
      transformations: 'Optional boons with a drawback',
      domains: 'How domains & cards work',
      cardLibrary: 'Every domain card, filterable',
      coreMechanics: 'Rolls, combat & actions',
      equipment: 'Weapons, armor & loot',
      itemsConsumables: 'Gear, relics & potions',
      beastforms: 'Druid beastform stat blocks',
      gmGuidance: 'Principles, Fear moves & handouts',
      adversaries: 'Monsters & stat blocks',
      environments: 'Scene environments & impulses',
      witherwildFrame: 'Campaign setting frame',
      kohd: 'Write and edit Kohd circuits',
    },
    introBlurb: 'Find rules, classes, ancestries, and GM Guidance, basically all the SRD in here. Use the sidebar or search bar to navigate. Some values or wording may differ from the physical book or the original source material if you spot something, use the bug report button at the bottom of the page.',
    makerGreeting: 'A note from the maker',
    makerText: "Hello, I'm Waleed. I built this to help me run and play Daggerheart and I hope you find it useful and enjoy using it at your table.\nYour feedback and support are always welcome!",
  },

  // ── Introduction ─────────────────────────────────────────────────────
  introduction: {
    title: 'Introduction',
    desc: 'Welcome to Daggerheart, a collaborative fantasy tabletop roleplaying game.',
  },

  // ── Character Creation ───────────────────────────────────────────────
  characterCreation: {
    title: 'Character Creation',
    desc: 'Follow these 9 steps to build your Daggerheart character.',
  },

  // ── Classes ──────────────────────────────────────────────────────────
  classes: {
    title: 'Classes',
    desc: 'Choose an archetype that determines your domains, features, and play style.',
    notFound: 'Class not found',
    filterDomainAria: 'Filter by domain',
    filterDomainLabel: 'Domain:',
    allDomains: 'All Domains',
    backToClasses: '← Back to Classes',
    stats: {
      baseEvasion: 'Base Evasion',
      startingHp: 'Starting HP',
      subclasses: 'Subclasses',
      domains: 'Domains',
      traitSpread: 'Trait Spread',
    },
    sections: {
      about: 'About',
      hopeFeature: 'Hope Feature',
      classFeature: 'Class Feature',
      classFeatures: 'Class Features',
      subclasses: 'Subclasses',
      characterGuide: 'Character Guide',
    },
    subclassNote: 'Choose one subclass at character creation. Each subclass unlocks abilities at Foundation, Specialization, and Mastery tiers as you level up.',
    subclassTiers: {
      foundation: 'Foundation',
      specialization: 'Specialization',
      mastery: 'Mastery',
    },
    spellcast: 'Spellcast:',
    startingItems: 'Starting Items:',
    suggestedLoadout: {
      title: 'Suggested Starting Loadout',
      primary: 'Primary Weapon',
      secondary: 'Secondary',
      armor: 'Armor',
    },
    charGuide: {
      backgroundTitle: 'Background Questions',
      backgroundHint: 'Answer any of the following. You can also create your own.',
      connectionsTitle: 'Connections',
      connectionsHint: 'Ask a fellow player one of these for their character to answer.',
    },
    tooltip: {
      named: 'Named',
      viewCta: '⬅ Click to view in Equipment',
    },
  },

  // ── Ancestries ───────────────────────────────────────────────────────
  ancestries: {
    title: 'Ancestries',
    desc: "Your ancestry reflects your character's lineage, impacting their physicality and granting two unique features.",
    searchPlaceholder: 'Search ancestries…',
    searchAria: 'Search ancestries',
    backToList: '← Back to Ancestries',
  },

  // ── Communities ──────────────────────────────────────────────────────
  communities: {
    title: 'Communities',
    desc: 'Your community reflects your social background, upbringing, and the culture that shaped you.',
    backToList: '← Back to Communities',
  },

  // ── Domains ──────────────────────────────────────────────────────────
  domains: {
    title: 'Domains',
    desc: 'Domains are the thematic categories of domain cards. Each class draws from two domains to build their loadout. There are 11 domains and 231 cards in the core set.',
    noCardsMatch: 'No cards match the current filters.',
  },

  // ── Core Mechanics ───────────────────────────────────────────────────
  mechanics: {
    title: 'Core Mechanics',
    desc: 'The rules that govern how Daggerheart works at the table.',
    contents: 'Contents',
    overview: 'Overview',
    backBtn: 'Back',
  },

  // ── GM Guidance ──────────────────────────────────────────────────────
  gm: {
    title: 'GM Guidance',
    desc: 'Tools, principles, and mechanics to help Game Masters run great Daggerheart sessions.',
    contents: 'Contents',
    tabs: {
      principles: 'Principles',
      fearMoves: 'Fear Moves',
      gmMechanics: 'GM Mechanics',
      adversaries: 'Adversaries',
      villainBuilder: 'Villain Builder',
      doomTrack: 'Doom Track',
    },
  },

  // ── Adversaries ──────────────────────────────────────────────────────
  adversaries: {
    title: 'Adversaries',
    desc: 'Full stat blocks from the Daggerheart SRD. Pick an adversary from the list to see its stat block, features, and tactics.',
    searchPlaceholder: 'Search adversaries…',
    searchAria: 'Search adversaries',
    noMatch: 'No adversaries match the current filters.',
    resetTitle: 'Reset all filters and sorting',
    stats: {
      difficulty: 'Difficulty',
      hp: 'HP',
      stress: 'Stress',
      thresholds: 'Thresholds',
      atk: 'ATK',
      experience: 'Experience',
      none: 'None',
    },
    motivesLabel: 'Motives & Tactics:',
    featuresTitle: 'Features',
    backToList: 'Back to list',
    tierLabel: (n: number) => `Tier ${n}`,
    sortCols: {
      tier: 'Tier',
      name: 'Name',
      role: 'Role',
      difficulty: 'Difficulty',
      hp: 'HP',
    },
  },

  // ── Equipment ────────────────────────────────────────────────────────
  equipment: {
    title: 'Equipment',
    desc: (wCount: number, aCount: number) =>
      `All weapons and armor from the Daggerheart SRD, ${wCount} weapons, ${aCount} armor pieces.`,
    backTo: (label: string) => `← Back to ${label}`,
    tabs: {
      weapons: (n: number) => `Weapons (${n})`,
      armor: (n: number) => `Armor (${n})`,
      frames: (n: number) => `Frames (${n})`,
    },
    weapons: {
      searchPlaceholder: 'Search weapons…',
      searchAria: 'Search weapons',
      noMatch: 'No weapons match the current filters.',
      countLabel: (n: number, total: number) => `${n} of ${total} weapons`,
      filterLabels: {
        category: 'Category',
        trait: 'Trait',
        type: 'Type',
        damage: 'Damage',
        range: 'Range',
        tier: 'Tier',
      },
      tableHeaders: {
        name: 'Name',
        tier: 'T',
        trait: 'Trait',
        range: 'Range',
        damage: 'Damage',
        burden: 'Burden',
        feature: 'Feature',
      },
    },
    armor: {
      searchPlaceholder: 'Search armor…',
      searchAria: 'Search armor',
      noMatch: 'No armor matches the current filters.',
      filterLabels: {
        tier: 'Tier',
        type: 'Type',
      },
      tableHeaders: {
        name: 'Name',
        tier: 'T',
        major: 'Major',
        severe: 'Severe',
        score: 'Score',
        feature: 'Feature',
      },
    },
    frames: {
      searchPlaceholder: 'Search frames…',
      searchAria: 'Search frames',
      noMatch: 'No frames match the current filters.',
      tableHeaders: {
        name: 'Name',
        tier: 'T',
        trait: 'Trait',
        range: 'Range',
        damage: 'Damage',
        burden: 'Burden',
        feature: 'Feature',
      },
    },
  },

  // ── Items & Consumables ──────────────────────────────────────────────
  items: {
    title: 'Items & Consumables',
    desc: 'Magical gear, relics, crafting recipes, and single-use consumables from the Daggerheart SRD. Items are sorted by their loot table roll number, lower rolls are more common, higher rolls are rarer.',
    tabs: {
      items: (n: number) => `Items (${n})`,
      consumables: (n: number) => `Consumables (${n})`,
    },
    items: {
      searchPlaceholder: 'Search items…',
      searchAria: 'Search items',
      noMatch: 'No items match.',
      filterLabels: {
        type: 'Type',
      },
      tableHeaders: {
        roll: '#',
        name: 'Name',
        type: 'Type',
        effect: 'Effect',
      },
    },
    consumables: {
      searchPlaceholder: 'Search consumables…',
      searchAria: 'Search consumables',
      noMatch: 'No consumables match.',
      filterLabels: {
        type: 'Type',
      },
      tableHeaders: {
        roll: '#',
        name: 'Name',
        type: 'Type',
        effect: 'Effect',
      },
    },
    resetTitle: 'Reset all filters',
  },

  // ── Beastforms ───────────────────────────────────────────────────────
  transformations: {
    title: 'Transformations',
    desc: 'Optional augmentations granted by the GM or taken at character creation. Each provides a mechanical benefit and a drawback. You can only have one transformation per PC.',
  },
  beastforms: {
    title: 'Beastforms',
    desc: 'Druids can transform into powerful beast shapes using Beastform. Each form provides a trait bonus, evasion bonus, attack, a set of advantages, and unique features. Pick a form from the list to see its full details.',
    searchPlaceholder: 'Search beastforms…',
    searchAria: 'Search beastforms',
    noMatch: 'No beastforms match.',
    resetTitle: 'Reset filters',
    advantageLabel: 'Gain advantage on:',
    featuresTitle: 'Features',
    backToList: 'Back to list',
    sortCols: {
      tier: 'Tier',
      name: 'Name',
      trait: 'Trait',
      damage: 'Damage',
      evasion: 'Evasion',
    },
  },

  // ── Environments ─────────────────────────────────────────────────────
  environments: {
    title: 'Environments',
    desc: 'Curated environments from the Daggerheart SRD, each with features, impulses, and potential adversaries.',
    searchPlaceholder: 'Search environments…',
    searchAria: 'Search environments',
    noMatch: 'No environments match the current filters.',
    resetTitle: 'Reset all filters and sorting',
    impulsesLabel: 'Impulses:',
    difficultyLabel: 'Difficulty',
    potentialAdversariesLabel: 'Potential Adversaries',
    featuresTitle: 'Features',
    backToList: 'Back to list',
    sortCols: {
      tier: 'Tier',
      name: 'Name',
      type: 'Type',
      difficulty: 'Difficulty',
      features: 'Features',
    },
    typeDescs: {
      Exploration: 'Wondrous locations filled with mysteries, wonders, and secrets to discover.',
      Social: 'Locations or gatherings centered on interpersonal dynamics and NPC relationships.',
      Traversal: 'Dangerous terrain where movement itself is the primary challenge.',
      Event: 'Special activities or occurrences rather than physical spaces.',
    },
  },

  // ── Kohd writer ──────────────────────────────────────────────────────
  kohd: {
    kicker: 'Motherboard campaign frame',
    title: 'Kohd Writer',
    desc: 'Type a sentence and it is drawn as a Kohd circuit: each word becomes a glyph on a 3x3 node grid, and small words become symbols on the traces that link them. Select a trace to bend it, or drag a word to move it.',
    inputLabel: 'Kohd text',
    consoleLabel: 'Kohd console',
    consolePath: 'kohd://console',
    placeholder: 'The network carries communication from city to city.',
    statWords: 'Words',
    statNodes: 'Nodes',
    statTraces: 'Traces',
    zoom: 'Zoom',
    hint: 'Click a trace (any line) to select it. Its corners show solid dots: drag one to move that corner, double-click or double-tap it to remove it. Drag a hollow dot to add a corner. On a trace between two circles, drag a diamond to slide that end around its circle. Drag a circle to move its word. Drag empty space to pan; scroll or pinch to zoom.',
    edit: 'Edit',
    view: 'View',
    share: 'Share',
    undo: 'Undo',
    redo: 'Redo',
    resetSelected: 'Reset selected',
    resetAll: 'Reset all edits',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    fit: 'Fit to view',
    labels: 'Word labels',
    exportSvg: 'SVG',
    exportPng: 'PNG',
    copyLink: 'Copy link',
    linkCopied: 'Link copied. It carries the text; bends and moves stay on this device.',
    copyFailed: 'Could not copy the link.',
    legendTitle: 'Reading Kohd',
    partsTitle: 'Circuit parts',
    lexiconTitle: 'Lexicon',
    parts: {
      charge: ['Charge', 'Resistor on the node of the first letter.'],
      ground: ['Ground', 'On the node of the last letter; its dots ride the ground line.'],
      nullMod: ['Null modifier', 'Fixes the grid when a word leaves a whole row or column empty.'],
      coupler: ['Coupler', 'Starts each sentence circuit.'],
      the: ['"The"', 'A line inside the charge node.'],
      a: ['"A" / "An"', 'A plus inside the charge node.'],
      comma: ['Comma', 'Two lines across a link.'],
      period: ['Period', 'Bar and dot at the end of the sentence.'],
      exclamation: ['Exclamation', 'Bar and triangle.'],
      question: ['Question', 'Bar and hook.'],
    },
    plural: 'plural',
    credit: 'Based on the Writing in Kohd rules. Inspired by kohd.dev.',
  },

  // ── Witherwild Campaign Frame ────────────────────────────────────────
  campaignFrame: {
    title: 'The Witherwild',
    desc: 'A Daggerheart campaign frame by Carlos Cisco, Rowan Hall, & Spenser Starke.',
    contents: 'Contents',
    tabs: {
      overview: 'Overview',
      communities: 'Communities',
      classesAncestries: 'Classes & Ancestries',
      principles: 'Principles',
      distinctions: 'Distinctions',
      campaign: 'Campaign',
    },
  },

} as const;

export type Strings = typeof en;
